// liveListReducer
// ---------------
// The pure delta-apply reducer behind useLiveList (live-lists overhaul, Batch C1).
// A Supabase Realtime `postgres_changes` event carries a single changed row; this
// folds that one change into the in-memory list a screen already holds, instead of
// re-fetching the whole table. Kept pure + framework-free so every branch (insert /
// update / delete / moved-out-of-scope / out-of-order) is unit-testable without React
// or a live socket.
//
// Scope note: one table (e.g. request_tasks) backs several lists (responder / picker /
// top-up), so `rowBelongs` decides whether a changed row is relevant to THIS list. An
// UPDATE can move a row in or out of scope, so the reducer adds or drops accordingly.
//
// Convergence note: Realtime can, rarely, deliver events out of order or drop one across
// a reconnect. The reducer is last-write-wins and never throws on a missing/absent row;
// the authoritative correction is the reconcile fetch useLiveList runs on (re)connect and
// on its fallback poll. So this reducer optimises the common path; it is not the source
// of truth.

export type LiveEventType = "INSERT" | "UPDATE" | "DELETE";

export interface LiveEvent<T> {
  eventType: LiveEventType;
  new?: T | null;
  old?: Partial<T> | null;
}

export interface DeltaOptions<T> {
  // Is this changed row relevant to this particular list? (e.g. is it a responder-type
  // task, for the Responders list?)
  rowBelongs: (row: T) => boolean;
  // Stable identity for a row (full or the partial `old` from a DELETE).
  getId: (row: Partial<T>) => string | number | null | undefined;
  // Optional ordering; applied after an add/replace so a new/updated row lands in place.
  sort?: (a: T, b: T) => number;
}

// Fold one Realtime event into `rows`. Returns the SAME `rows` reference when nothing
// changed (so callers/React can bail the re-render); otherwise a new array.
export function applyDelta<T>(rows: T[], event: LiveEvent<T>, opts: DeltaOptions<T>): T[] {
  const { rowBelongs, getId, sort } = opts;
  const sorted = (arr: T[]): T[] => (sort ? arr.slice().sort(sort) : arr);

  if (event.eventType === "DELETE") {
    const id = event.old ? getId(event.old) : null;
    if (id == null) return rows;
    const has = rows.some((r) => getId(r) === id);
    return has ? rows.filter((r) => getId(r) !== id) : rows;
  }

  // INSERT or UPDATE — driven by the new row.
  const newRow = event.new;
  const id = newRow ? getId(newRow) : null;
  if (newRow == null || id == null) return rows;

  const idx = rows.findIndex((r) => getId(r) === id);

  if (!rowBelongs(newRow)) {
    // Moved out of this list's scope (e.g. its type changed) → drop it if we hold it.
    return idx < 0 ? rows : rows.filter((r) => getId(r) !== id);
  }
  if (idx < 0) {
    // New to this list — a fresh INSERT, or an UPDATE that brought it into scope.
    return sorted(rows.concat(newRow));
  }
  // Replace the row we already hold, then re-order (the update may have changed a sort key).
  const copy = rows.slice();
  copy[idx] = newRow;
  return sorted(copy);
}
